import type { Middleware } from 'openapi-fetch';
import { toast } from 'sonner';
import { UndoStack } from './UndoStack';
import { editorWrites, sceneWriteMiddleware, sceneWriteRecorder, type ScenePorts, type SceneWriteRecorder } from './sceneWrites';
import { failedWrites } from './failedWrites';

/** The editor's renderer bridge, its undo stack, and the recorder that puts the client's scene writes on that stack. */
export interface EditorUndo<B> {
  bridge: B;
  undo: UndoStack;
  sceneWrites: SceneWriteRecorder;
}

const sessions = new WeakMap<object, EditorUndo<unknown>>();

/**
 * The bridge, undo stack and recorder of the editor that writes through `client`, made together once however
 * many times services are created (StrictMode runs the factory twice), so the stack the editor undoes is the
 * one its writes are recorded on.
 */
export function editorUndo<B>(
  client: { use(...middleware: Middleware[]): void },
  createBridge: () => B,
  portsFor: (bridge: NoInfer<B>, live: () => boolean) => ScenePorts,
): EditorUndo<B> {
  const existing = sessions.get(client) as EditorUndo<B> | undefined;
  if (existing) return existing;
  const bridge = createBridge();
  const undo = new UndoStack();
  // An undo or redo that fails is put back as it was where it can be, its failed write shown as saved, and the user is told either way
  undo.failures$.subscribe(({ label, action, complete }) => {
    if (complete) toast.error(`Couldn't ${action} "${label}". The scene shows what is saved; check your connection and try again.`);
    else toast.error(`Couldn't fully ${action} "${label}". Part of it may still be changed; check the scene.`);
  });
  // Undo and redo update the renderer and the editor only for the scene they were pressed in
  const sceneWrites = sceneWriteRecorder(undo, portsFor(bridge, () => undo.inOpenScene()));
  // A write that fails is told once per gesture or burst, and its rows are shown as the server has them
  const failed = failedWrites({
    settled: gestureIds => undo.settled(gestureIds),
    reread: failure => sceneWrites.ports.reread.failedWrite(failure),
    open: sceneId => sceneWrites.scene() === sceneId,
  });
  // What a write saves is what the editor knows of its rows; its writes in flight are the editor's, which re-reads of the scene wait for
  client.use(sceneWriteMiddleware(sceneWrites, {
    failed,
    saved: (write, body, answer) => sceneWrites.ports.known.saved(write, body, answer),
    writes: editorWrites,
  }));
  const session = { bridge, undo, sceneWrites };
  sessions.set(client, session);
  return session;
}
